/* Maison Byōbu · le cadreur
   Le visiteur saisit son mur, fait glisser le cadre sur l'œuvre, voit son mur et son prix, puis
   « Je compose mon mur » recopie les dimensions dans les champs Apippa (qui fixent le prix de
   la ligne) et ajoute au formulaire produit la propriété _cadrage (JSON pour l'atelier) et deux propriétés
   visibles au paiement et dans les e-mails : « Votre mur » (« 300 × 250 cm ») et « Cadrage »
   (« Tout le mur » ou « L’œuvre entière »).
   Géométrie (brief 3.2) : a = largeur/hauteur de l'œuvre, r = largeur/hauteur du mur.
   r <= a : le cadre prend toute la hauteur, largeur r/a, il glisse horizontalement.
   r >  a : le cadre prend toute la largeur, hauteur a/r, il glisse verticalement. */

const MB_BORNES = { lMin: 50, lMax: 1500, hMin: 50, hMax: 400 };
const MB_MARGE = 0; // 16/10 (brief « site qui convainc », section 6, Marcel) : le site n'ajoute plus de marge, la taille saisie est la taille imprimée et payée ; le client ajoute lui-même ses 10 cm (avant : 10, depuis le 02/10)
const MB_ENTIERE = { hMin: 50, hMax: 610 }; // hauteur du panneau « L'œuvre entière » : en dehors, la carte est grisée (hMax n'agit pas aujourd'hui : le panneau tient dans un mur de 410 cm au plus)
const MB_SEUIL_ENTIER = 0.95; // au-delà de 95 % de l'œuvre visible, une seule carte
const MB_LE_MM = 762; // largeur d'un lé chez l'imprimeur : 76,2 cm (30 pouces), le dernier lé prend le reste
const MB_SILHOUETTE_CM = 175;
const mbEuros = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });
/* Réductions automatiques « Tarif grands murs » (admin Shopify, non cumulables), du palier le plus haut au plus bas ; seuils en centimes sur le sous-total. */
const MB_PALIERS = [{ seuil: 250000, pct: 20 }, { seuil: 150000, pct: 15 }];
/* Réductions automatiques sur les murs (admin, classe produit, collection La sélection), de la plus forte à la plus faible :
   « Trois œuvres ou plus — 20 % » (05/10, combinable avec la livraison seulement) et « Deux murs ou plus — 15 % ». */
const MB_REMISES_MURS = [{ murs: 3, pct: 20, nom: 'Trois œuvres' }, { murs: 2, pct: 15, nom: 'Deux murs' }];
/* Lus aussi par le parcours en étapes (assets/mb-achat-etapes.js, « Vos avantages ») : une seule source pour les remises. */
window.mbRemises = { paliers: MB_PALIERS, murs: MB_REMISES_MURS };
/* Finesse minimale du fichier d'impression : 25 DPI sur la source, soit 100 DPI après l'agrandissement IA ×4 (05/10).
   Au-delà, le cadreur bloque l'ajout et propose le designer. */
const MB_DPI_SOURCE = 25;
const mbEntier = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 });
const mbDeci = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const mbCm = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 1 });
const mbM = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
/* 24/10 (spec moteur-delai-prix, A) : prix encaissé = prix affiché. Apippa, le calculateur qui fixe le prix de la ligne, se
   charge après la page : le moteur l'attend MB_APIPPA_ATTENTE ms après son démarrage. Passé ce délai sans ses champs, l'ajout
   est bloqué (bouton désactivé, message) ; s'ils arrivent plus tard, le bouton se rouvre seul. Jamais d'ajout sans Apippa. */
const MB_APIPPA_ATTENTE = 15000;

/* Le devis d'un projet, au centime comme Shopify (vérifié le 05/10 sur un vrai panier) : une seule réduction, la plus
   avantageuse. Réduction sur les murs : tronquée au centime pour chaque unité (deux têtes de lit à 261,66 € et −15 % :
   2 × 39,24 €). Tarif grands murs : tronqué au centime sur le sous-total avant réduction, échantillon compris.
   murs : [{ centimes (prix unitaire plein tarif), quantite }] ; autres : centimes des articles qui ne sont pas des murs. */
function mbDevis(murs, autres = 0) {
  const nb = murs.reduce((s, m) => s + m.quantite, 0);
  const sousTotal = murs.reduce((s, m) => s + m.centimes * m.quantite, 0) + autres;
  const options = [];
  const rm = MB_REMISES_MURS.find((r) => nb >= r.murs);
  if (rm) options.push({ nom: rm.nom, pct: rm.pct, montant: murs.reduce((s, m) => s + Math.floor((m.centimes * rm.pct) / 100) * m.quantite, 0) });
  const p = MB_PALIERS.find((x) => sousTotal >= x.seuil);
  if (p) options.push({ nom: 'Tarif grands murs', pct: p.pct, montant: Math.floor((sousTotal * p.pct) / 100) });
  const remise = options.sort((a, b) => b.montant - a.montant)[0] || null;
  return { nb, sousTotal, remise, total: sousTotal - (remise ? remise.montant : 0) };
}

/* 24/10, relecture (défaut 2) : dernier contrôle sur la requête d'ajout elle-même, juste avant l'envoi. Apippa ne calcule son
   jeton (_cpc_custom_cart_item, « none » jusque-là) qu'après le clic, puis envoie lui-même /cart/add. Une ligne de mur sans
   jeton valide, ou sans largeur, hauteur ou surface, serait encaissée au prix d'un seul m² : elle n'est pas envoyée (aucune
   requête ne part ; rien n'est retiré du panier après coup). Ligne de mur : une variante du produit de la page (formulaire
   produit, matières du moteur) ou toute ligne qui porte un champ d'Apippa. L'échantillon (autre produit) n'est pas concerné. */
function mbLignesAjout(corps) {
  const depuis = (paires) => {
    const l = { id: '', props: {} };
    for (const [k, v] of paires) {
      if (k === 'id') l.id = String(v);
      const m = /^properties\[(.*)\]$/.exec(k);
      if (m) l.props[m[1]] = String(v);
    }
    return [l];
  };
  if (typeof FormData !== 'undefined' && corps instanceof FormData) return depuis([...corps.entries()]);
  if (corps instanceof URLSearchParams) return depuis([...corps.entries()]);
  if (typeof corps === 'string') {
    const s = corps.trim();
    if (s.startsWith('{')) {
      const j = JSON.parse(s);
      return (Array.isArray(j.items) ? j.items : [j]).map((it) => ({ id: String(it.id || ''), props: it.properties || {} }));
    }
    return depuis([...new URLSearchParams(s).entries()]);
  }
  return null; // corps illisible (Blob, flux) : aucun chemin du thème n'en envoie
}

function mbLigneRefusee(l, variantesMur) {
  const cles = Object.keys(l.props);
  const champ = (re) => { const k = cles.find((x) => re.test(x)); return k == null ? '' : String(l.props[k]).trim(); };
  const apippa = cles.some((k) => /^(largeur imprim|hauteur imprim|surface|_cpc_custom_cart_item$)/i.test(k));
  if (!apippa && !variantesMur.has(l.id)) return false; // pas un mur
  const nombre = (v) => parseFloat(String(v).replace(',', '.')) > 0;
  const jeton = champ(/^_cpc_custom_cart_item$/i);
  return !jeton || jeton.toLowerCase() === 'none' || !nombre(champ(/^largeur imprim/i)) || !nombre(champ(/^hauteur imprim/i)) || !nombre(champ(/^surface/i));
}

function mbGarderEnvoi() {
  if (window.mbGardeEnvoi) return;
  window.mbGardeEnvoi = true;
  const estAjout = (url, methode) => String(methode || 'GET').toUpperCase() === 'POST' && /\/cart\/add(\.js)?(\?|$)/.test(String(url || ''));
  const variantesMur = () => {
    const ids = new Set();
    document.querySelectorAll('mb-cadreur [data-mb-matieres] input[type="radio"]').forEach((r) => ids.add(String(r.value)));
    const f = document.querySelector('form[data-type="add-to-cart-form"]');
    if (f) f.querySelectorAll('[name="id"], [name="oldId"]').forEach((i) => { if (i.value) ids.add(String(i.value)); });
    return ids;
  };
  const refuser = (corps) => {
    let lignes = null;
    try { lignes = mbLignesAjout(corps); } catch (e) { lignes = null; }
    if (!lignes) return false;
    const ids = variantesMur();
    return lignes.some((l) => mbLigneRefusee(l, ids));
  };
  const signaler = () => {
    console.warn('[mb-cadreur] Ajout refusé avant l’envoi : ligne de mur sans jeton Apippa valide, ou sans largeur, hauteur ou surface.');
    document.dispatchEvent(new CustomEvent('mb-cadreur:envoi-refuse'));
  };
  const ouvrirXhr = XMLHttpRequest.prototype.open;
  const envoyerXhr = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (methode, url) {
    this.mbAjout = estAjout(url, methode);
    return ouvrirXhr.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (corps) {
    if (this.mbAjout && refuser(corps)) {
      signaler();
      const xhr = this; // comme une coupure réseau : requête terminée, statut 0, rien d'envoyé
      setTimeout(() => {
        Object.defineProperty(xhr, 'readyState', { configurable: true, value: 4 });
        Object.defineProperty(xhr, 'status', { configurable: true, value: 0 });
        for (const type of ['readystatechange', 'error', 'loadend']) xhr.dispatchEvent(new Event(type));
      }, 0);
      return undefined;
    }
    return envoyerXhr.apply(this, arguments);
  };
  const recuperer = window.fetch;
  window.fetch = function (entree, options) {
    const url = typeof entree === 'string' ? entree : (entree && entree.url) || '';
    const methode = (options && options.method) || (entree && typeof entree === 'object' && entree.method) || 'GET';
    if (estAjout(url, methode) && refuser(options && options.body)) {
      signaler();
      return Promise.reject(new TypeError('Ajout refusé : ligne de mur sans jeton Apippa valide.'));
    }
    return recuperer.apply(this, arguments);
  };
}

class MbCadreur extends HTMLElement {
  connectedCallback() {
    if (this.pret) return;
    this.pret = true;
    const q = (s) => this.querySelector(s);
    this.a = parseFloat(this.dataset.ratio) || 2;
    this.oeuvre = this.dataset.oeuvre || '';
    const prixM2 = parseFloat(this.dataset.prixM2);
    this.prixM2 = prixM2 > 0 ? prixM2 : null; // prix du produit (Shopify), jamais de valeur de repli
    this.minM2 = parseFloat(this.dataset.minM2) || 1;
    this.defaut = { x: parseFloat(this.dataset.x) || 0.5, y: parseFloat(this.dataset.y) || 0.5 };
    this.contact = this.dataset.contact || '/pages/contact';
    this.srcL = parseInt(this.dataset.sourceL, 10) || 0; // pixels du fichier d'impression (snippets/mb-oeuvre-infos.liquid, champ source)
    this.srcH = parseInt(this.dataset.sourceH, 10) || 0;
    this.panierMurs = []; this.panierAutres = 0; this.ajoute = false; this.horsLimite = false;
    this.el = {
      L: q('[data-mb-largeur]'), H: q('[data-mb-hauteur]'), erreur: q('[data-mb-erreur]'),
      vue: q('.mb-cadreur__vue'), oeuvre: q('.mb-cadreur__oeuvre'), cadre: q('.mb-cadreur__cadre'),
      murZone: q('.mb-cadreur__mur-zone'), mur: q('.mb-cadreur__mur'), les: q('.mb-cadreur__les'), decoupe: q('[data-mb-decoupe]'),
      prix: q('[data-mb-prix]'), vivant: q('[data-mb-prix-vivant]'), barre: q('[data-mb-prix-barre]'), remise: q('[data-mb-remise]'), surface: q('[data-mb-surface]'), recap: q('[data-mb-recap]'), verif: q('[data-mb-verif]'),
      rendu: q('[data-mb-rendu]'), cartes: [...this.querySelectorAll('[data-mb-carte]')], panneau: q('.mb-cadreur__panneau'), etape3: q('[data-mb-etape3]'), consigne3: q('[data-mb-consigne3]'),
      bouton: q('[data-mb-ajouter]'), message: q('[data-mb-message]'),
      phrase: q('[data-mb-phrase]'), jauge: q('[data-mb-jauge]'), rempli: q('[data-mb-jauge-rempli]'), reperes: q('[data-mb-jauge-reperes]'), phraseJauge: q('[data-mb-jauge-phrase]'),
      coteL: q('[data-mb-cote-l]'), coteH: q('[data-mb-cote-h]'), exemple: q('[data-mb-legende-exemple]'), formats: [...this.querySelectorAll('[data-mb-format]')],
      projet: q('[data-mb-projet]'), limite: q('[data-mb-limite]'), trois: q('[data-mb-trois]'), autre: q('[data-mb-autre-piece]'),
    };
    /* Les liens « écrivez-nous » du designer reçoivent l'e-mail prérempli au clic, depuis l'état du moment ;
       « Habiller une autre pièce » repart des mesures en gardant le panier. */
    this.addEventListener('click', (e) => {
      const lien = e.target.closest('[data-mb-ecrire]');
      if (lien) lien.href = this.mailto();
      if (e.target.closest('[data-mb-autre]')) this.autrePiece();
    });
    if (this.el.autre && new URLSearchParams(location.search).get('piece') === 'autre') this.el.autre.hidden = false; // retour depuis le panier
    this.urlMur = `url("${this.dataset.image}")`;
    this.el.mur.style.backgroundImage = this.urlMur;
    if (this.el.panneau) this.el.panneau.style.backgroundImage = this.urlMur;
    this.L = null; this.H = null; this.gL = null; this.gH = null; // gL, gH : le mur dessiné (le vôtre, ou l'exemple tant que vous n'avez rien saisi)
    this.x0 = 0; this.y0 = 0; this.fw = 1; this.fh = 1; this.mode = 'h';
    this.rendu = 'tout'; this.bx = 0.5; this.by = 0.5; this.imp = null;
    this.dataset.etat = 'attente';

    for (const champ of [this.el.L, this.el.H]) {
      champ.addEventListener('input', () => this.lire(false));
      champ.addEventListener('change', () => this.lire(true));
      champ.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); this.lire(true); } });
    }
    this.el.verif.addEventListener('change', () => { if (this.el.verif.checked) this.message(''); });
    for (const r of this.querySelectorAll('[data-mb-rendu] input[type="radio"]')) r.addEventListener('change', () => { if (r.checked) this.choisirRendu(r.value); });
    for (const c of this.el.cartes) c.style.setProperty('--mb-carte-img', `url("${this.dataset.image}")`);
    this.el.bouton.addEventListener('click', () => this.ajouter());
    this.matieres();
    this.formats();
    this.reperes();
    this.glisser();
    this.glisserPanneau();
    this.clavier();
    this.panier();
    mbGarderEnvoi();
    this.guetterApippa();
    this.voirChezMoi();
    if ('ResizeObserver' in window) { const o = new ResizeObserver(() => this.mesurer()); o.observe(this.el.vue); if (this.el.murZone) o.observe(this.el.murZone); }
    this.mesurer();
    this.lire(false);
    this.lirePanier();
    window.addEventListener('pageshow', (e) => { if (e.persisted) this.lirePanier(); }); // retour arrière depuis le panier
  }

  /* ---------- La matière (chantier « Les matières », 09/10) ----------
     Une carte par variante (snippets/mb-matieres.liquid). Le prix au m² suit la variante choisie ; le formulaire produit
     reçoit son identifiant et Apippa est prévenu par variant:changed (son prix de base = prix de la variante). */
  matieres() {
    this.matiere = null;
    const radios = [...this.querySelectorAll('[data-mb-matieres] input[type="radio"]')];
    for (const r of radios) r.addEventListener('change', () => { if (r.checked) this.choisirMatiere(r); });
    const choisie = radios.find((r) => r.checked);
    if (choisie) this.choisirMatiere(choisie, true);
  }

  choisirMatiere(r, depart = false) {
    const centimes = parseInt(r.dataset.prix, 10);
    if (!(centimes > 0)) return;
    if (!depart && (!this.matiere || this.matiere.id !== r.value)) { this.ajoute = false; this.message(''); } // une autre matière : un autre mur, pas encore au panier
    this.matiere = { id: r.value, nom: r.dataset.nom || '', centimes };
    this.prixM2 = centimes / 100;
    const m2 = this.querySelector('[data-mb-m2]');
    if (m2) m2.textContent = `${mbEuros.format(this.prixM2).replace(/[,.]00(?=\D)/, '')} / m²`;
    const min = this.querySelector('[data-mb-minimum]');
    if (min) min.textContent = `${String(this.minM2).replace(".", ",")} m² minimum, soit ${mbEuros.format((centimes * this.minM2) / 100).replace(/[,.]00(?=\D)/, '')}`;
    this.synchroniserVariante();
    if (!depart) { this.majCartes(); this.afficherPrix(); }
  }

  /* Le formulaire Horizon (caché) et Apippa reçoivent la variante choisie. Jamais d'ajout sans cette étape. */
  synchroniserVariante() {
    if (!this.matiere) return true;
    const form = document.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return false;
    /* Apippa renomme le champ d'origine en « oldId » et le relit au moment de l'ajout (sa variante de base) : on écrit la
       matière dans les deux, sinon la ligne partirait au prix de la matière choisie mais sous la variante du chargement
       (relevé le 10/10 : Sisal encaissé, « Intissé lisse » sur la commande). */
    const champs = [...form.querySelectorAll('input[name="id"], select[name="id"], input[name="oldId"], select[name="oldId"]')];
    if (!champs.length) return false;
    for (const champ of champs) champ.value = this.matiere.id;
    document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant: { id: Number(this.matiere.id), price: this.matiere.centimes, available: true } } }));
    return champs.every((champ) => String(champ.value) === String(this.matiere.id));
  }

  /* ---------- Le projet : les murs déjà dans le panier (05/10) ----------
     Prix plein tarif lus du panier Shopify (original_price, fixé par Apippa), jamais recalculés. Panier illisible :
     le devis ne compte que ce mur. */
  async lirePanier() {
    try {
      const racine = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const r = await fetch(`${racine}cart.js`, { headers: { Accept: 'application/json' } });
      if (r.ok) {
        const c = await r.json();
        const murs = []; let autres = 0;
        for (const i of c.items || []) {
          if (i.product_type === 'Papier peint panoramique' && i.handle !== 'echantillon') {
            murs.push({ nom: String(i.product_title || '').replace(/ — /g, ', '), mur: (i.properties && i.properties['Votre mur']) || '', centimes: i.original_price, quantite: i.quantity });
          } else autres += i.original_line_price || 0;
        }
        this.panierMurs = murs; this.panierAutres = autres;
      }
    } catch (e) { /* hors ligne, aperçu : rien */ }
    this.afficherPrix();
  }

  /* ---------- Dimensions ---------- */
  /* Centimètres entiers seulement : « 76,2 » ou « 3.5 » ne deviennent jamais 762 ou 35 cm (signalé le 30/09).
     Acceptés : « 350 », « 350 cm », « 350cm », « 1 500 » (espace des milliers). Refusés : virgule, point, « 3 m 50 ».
     Renvoie null si le champ est vide, NaN si la saisie n'est pas un nombre entier de centimètres. */
  valeur(champ) {
    const brut = String(champ.value).replace(/[  ]/g, ' ').trim().toLowerCase();
    if (brut === '') return null;
    const m = brut.match(/^(\d{1,2} \d{3}|\d+) ?(cm)?$/);
    return m ? parseInt(m[1].replace(/ /g, ''), 10) : NaN;
  }

  valider() {
    const L = this.valeur(this.el.L);
    const H = this.valeur(this.el.H);
    const B = MB_BORNES;
    const entiers = 'Indiquez des centimètres entiers, par exemple 350.';
    if (Number.isNaN(L)) return { ok: false, champ: this.el.L, texte: entiers };
    if (Number.isNaN(H)) return { ok: false, champ: this.el.H, texte: entiers };
    if (L === null && H === null) return { ok: false, champ: this.el.L, texte: 'Indiquez la largeur et la hauteur de votre mur, en centimètres.' };
    if (L === null) return { ok: false, champ: this.el.L, texte: 'Indiquez aussi la largeur de votre mur.' };
    if (H === null) return { ok: false, champ: this.el.H, texte: 'Indiquez aussi la hauteur de votre mur.' };
    if (L < B.lMin || L > B.lMax) return { ok: false, champ: this.el.L, texte: 'La largeur doit être comprise entre 50 et 1 500 cm.' };
    if (H > B.hMax) return { ok: false, champ: this.el.H, texte: this.texteHautMax() };
    if (H < B.hMin) return { ok: false, champ: this.el.H, texte: 'La hauteur doit être d’au moins 50 cm.' };
    return { ok: true, L, H };
  }

  /* Au-delà de la hauteur maximale : une seule phrase, pour valider() et pour les molettes */
  texteHautMax() {
    return `Au-delà de ${mbCm.format(MB_BORNES.hMax / 100)} m de haut, <a href="${this.contact}">écrivez-nous</a> : nous préparons votre projet.`;
  }

  lire(signaler) {
    const v = this.valider();
    if (!v.ok) {
      if (signaler && (this.el.L.value || this.el.H.value)) this.erreur(v.texte); else this.erreur('');
      if (this.dataset.etat === 'pret') { this.dataset.etat = 'attente'; this.L = null; this.H = null; }
      this.demonstration();
      this.pressoirs();
      return;
    }
    this.erreur('');
    const change = v.L !== this.L || v.H !== this.H;
    if (change && this.el.verif.checked) this.el.verif.checked = false; // dimensions changées : le client les reconfirme (décision du 30/09)
    if (change) this.ajoute = false; // un nouveau mur, qui n'est pas encore dans le panier
    const premier = this.dataset.etat !== 'pret';
    this.L = v.L; this.H = v.H;
    this.pressoirs();
    if (!change && !premier) return;
    this.dataset.etat = 'pret';
    this.geometrie(v.L, v.H, premier);
    this.afficherPrix();
    this.message('');
  }

  /* Le mur dessiné : les dimensions saisies, ou, tant qu'il n'y en a pas, l'exemple des champs (300 × 250) avec ce que le client
     a déjà tapé de valable. Rien n'est jamais commandé depuis l'exemple : this.L et this.H restent vides. */
  demonstration() {
    const B = MB_BORNES;
    const pris = (champ, min, max) => { const v = this.valeur(champ); return Number.isInteger(v) && v >= min && v <= max ? v : parseInt(champ.placeholder, 10) || min; };
    const L = pris(this.el.L, B.lMin, B.lMax);
    const H = pris(this.el.H, B.hMin, B.hMax);
    const garde = this.rendu; this.rendu = 'tout'; // l'exemple se dessine toujours « Tout le mur » ; le rendu choisi par le client est rendu ensuite
    if (L !== this.gL || H !== this.gH || this.dataset.exemple !== 'oui') this.geometrie(L, H, true);
    this.afficherPrix();
    this.rendu = garde;
  }

  geometrie(L, H, centrer) {
    const r = (L + MB_MARGE) / (H + MB_MARGE);
    const ancien = { mode: this.mode, cx: this.x0 + this.fw / 2, cy: this.y0 + this.fh / 2 };
    this.gL = L; this.gH = H;
    if (r <= this.a) { this.mode = 'h'; this.fw = r / this.a; this.fh = 1; }
    else { this.mode = 'v'; this.fw = 1; this.fh = this.a / r; }
    this.dataset.mode = this.mode;
    const centre = centrer || ancien.mode !== this.mode ? this.defaut : { x: ancien.cx, y: ancien.cy };
    this.x0 = this.borner(centre.x - this.fw / 2, 1 - this.fw);
    this.y0 = this.borner(centre.y - this.fh / 2, 1 - this.fh);
    this.calculer();
    this.ressort(true);
    this.mesurer();
    this.tracerLes();
  }

  borner(v, max) { return Math.min(Math.max(v, 0), Math.max(max, 0)); }

  /* ---------- Mise en page de la scène ---------- */
  mesurer() {
    const W = this.el.vue.clientWidth;
    if (!W) { this.poserMur(); return; } // scène masquée (rendu « œuvre entière » ou œuvre presque entière) : l'aperçu du mur se pose quand même
    const mobile = W < 700;
    const hMin = mobile ? 220 : 300;
    const hMax = mobile ? 360 : 560;
    const Hs = Math.round(Math.min(Math.max(W / this.a, hMin), hMax));
    const Aw = Hs * this.a;
    this.geo = { W, Hs, Aw, panoramique: Aw > W * 1.02 };
    this.el.vue.style.setProperty('--mb-vue-h', `${Hs}px`);
    this.el.oeuvre.style.setProperty('--mb-oeuvre-l', `${Aw}px`);
    this.el.oeuvre.style.setProperty('--mb-oeuvre-h', `${Hs}px`);
    this.poser();
    this.poserMur();
  }

  /* Position du cadre et de l'œuvre. Quand l'œuvre dépasse la scène (panoramas, mobile),
     le cadre reste au centre et c'est l'œuvre qui glisse dessous, comme un viseur. */
  poser(xAff = this.x0, yAff = this.y0) {
    if (!this.geo) return;
    const { W, Hs, Aw, panoramique } = this.geo;
    const cl = this.fw * Aw;
    const ch = this.fh * Hs;
    let tx = (W - Aw) / 2;
    if (panoramique) tx = W / 2 - (xAff + this.fw / 2) * Aw;
    const s = this.el.cadre.style;
    s.setProperty('--mb-cadre-l', `${cl}px`);
    s.setProperty('--mb-cadre-h', `${ch}px`);
    s.setProperty('--mb-cx', `${xAff * Aw}px`);
    s.setProperty('--mb-cy', `${yAff * Hs}px`);
    this.el.oeuvre.style.setProperty('--mb-tx', `${tx}px`);
    const pos = this.mode === 'h' ? (this.fw < 1 ? this.x0 / (1 - this.fw) : 0.5) : (this.fh < 1 ? this.y0 / (1 - this.fh) : 0.5);
    const pct = Math.round(pos * 100);
    this.el.cadre.setAttribute('aria-valuenow', String(pct));
    this.el.cadre.setAttribute('aria-valuetext', this.mode === 'h' ? `Cadrage à ${pct} % depuis la gauche de l’œuvre` : `Cadrage à ${pct} % depuis le haut de l’œuvre`);
    this.fondMur();
  }

  poserMur() {
    const zone = this.el.murZone;
    if (!zone || !this.gL || !this.gH) return;
    const grand = window.innerWidth >= 990;
    const largeurDispo = Math.max((zone.clientWidth || 320) - (grand ? 44 : 34), 120); // la place des cotes
    /* Sous 990 px, votre mur est un bandeau collant (04/10) : 200 px au plus, légende et marges comprises (46 px, mb.css),
       pour laisser les champs visibles au-dessus du clavier ; la zone du mur en prend tout le reste, cotes comprises (30 px). */
    /* 10/10 : la progression du parcours en étapes coiffe le bandeau ; le mur garde la même place (220 px au plus en tout) */
    const prog = grand ? null : this.querySelector('[data-mb-progression]');
    const hProg = prog ? prog.offsetHeight : 0;
    const bandeau = Math.min(200 + (hProg ? 20 : 0), Math.max(150, window.innerHeight * 0.3)) - 46 - hProg;
    const hMax = grand ? Math.min(520, Math.max(260, window.innerHeight * 0.58)) : bandeau - 30;
    zone.style.minHeight = `${Math.round(hMax) + 30}px`; // hauteur de la scène fixe : la page ne bouge pas quand le mur change de proportion
    const r = (this.gL + MB_MARGE) / (this.gH + MB_MARGE);
    let w = largeurDispo;
    let h = w / r;
    if (h > hMax) { h = hMax; w = h * r; }
    this.el.mur.style.setProperty('--mb-mur-l', `${Math.round(w)}px`);
    this.el.mur.style.setProperty('--mb-mur-h', `${Math.round(h)}px`);
    this.el.mur.style.setProperty('--mb-silh', `${Math.min(MB_SILHOUETTE_CM / this.gH, 1) * 100}%`);
    this.fondMur();
  }

  fondMur() {
    if (!this.gL) return;
    const s = this.el.mur.style;
    if (this.dataset.rendu === 'entiere' && this.pe && this.pe.ok) { // dataset.rendu : le rendu réellement dessiné (pas celui gardé pendant l'exemple)
      s.backgroundImage = 'none';
      this.poserPanneau();
      return;
    }
    s.backgroundImage = this.urlMur;
    { const l = this.el.les.style; l.left = '0'; l.width = '100%'; l.top = '0'; l.height = ''; l.bottom = '0'; }
    const x = this.borner(this.x0, 1 - this.fw);
    const y = this.borner(this.y0, 1 - this.fh);
    s.backgroundSize = `${100 / this.fw}% ${100 / this.fh}%`;
    s.backgroundPosition = `${this.fw < 1 ? (x / (1 - this.fw)) * 100 : 0}% ${this.fh < 1 ? (y / (1 - this.fh)) * 100 : 0}%`;
  }

  /* « L'œuvre entière » : le panneau dans le mur, déplaçable sur l'axe libre (visualisation seulement) */
  poserPanneau() {
    const { w: pw, h: ph } = this.pe, { w: Lp, h: Hp } = this.pc;
    const libreX = pw < Lp, libreY = ph < Hp;
    const gx = libreX ? this.bx * ((Lp - pw) / Lp) * 100 : 0;
    const gy = libreY ? this.by * ((Hp - ph) / Hp) * 100 : 0;
    const k = this.el.panneau.style;
    k.width = `${(pw / Lp) * 100}%`; k.height = `${(ph / Hp) * 100}%`;
    k.left = `${gx}%`; k.top = `${gy}%`;
    const l = this.el.les.style;
    l.left = `${gx}%`; l.width = `${(pw / Lp) * 100}%`; l.top = `${gy}%`; l.height = `${(ph / Hp) * 100}%`; l.bottom = 'auto';
  }

  /* ---------- Les deux rendus : « Tout le mur » et « L'œuvre entière » (02/10) ----------
     Les dimensions saisies sont celles du mur ; le site ajoute 10 cm de marge à chaque dimension qui touche un bord.
     Tout le mur : l'œuvre couvre le mur (cadre glissant, comme avant). L'œuvre entière : l'œuvre complète, sans déformation,
     à sa taille maximale dans le mur (marge ajoutée seulement du côté qui touche le bord). */
  calculer() {
    const Lp = this.gL + MB_MARGE, Hp = this.gH + MB_MARGE, r = Lp / Hp, a = this.a;
    this.visible = r <= a ? r / a : a / r;
    let pw; let ph;
    if (r >= a) { ph = Hp; pw = Math.round(Hp * a); } else { pw = Lp; ph = Math.round(Lp / a); }
    this.pc = { w: Lp, h: Hp };
    this.pe = { w: pw, h: ph, ok: ph >= MB_ENTIERE.hMin && ph <= MB_ENTIERE.hMax };
    this.unique = this.visible >= MB_SEUIL_ENTIER;
    if ((this.unique || !this.pe.ok) && this.rendu !== 'tout') { this.rendu = 'tout'; this.bx = 0.5; this.by = 0.5; }
    this.imp = this.rendu === 'entiere' ? { w: pw, h: ph } : this.pc;
    this.dataset.rendu = this.rendu;
    this.dataset.unique = this.unique ? 'oui' : 'non';
    this.majCartes();
  }

  choisirRendu(v) {
    if (this.dataset.etat !== 'pret' || (v === 'entiere' && (this.unique || !this.pe.ok))) return;
    if (v !== this.rendu && this.el.verif.checked) this.el.verif.checked = false; // le rendu change le prix et la taille : le client reconfirme
    if (v !== this.rendu) this.ajoute = false;
    this.rendu = v;
    this.calculer();
    this.mesurer();
    this.tracerLes();
    this.afficherPrix();
    this.majVoirChezMoi();
  }

  /* Tarif grands murs (réductions automatiques de l'admin, non cumulables) : seuils sur le sous-total, en centimes.
     Le prix réduit se calcule comme Shopify (remise arrondie au centime), pour égaler le total du panier. */
  /* prixM2 (10/10) : le total d'une autre matière pour le même mur (cartes de l'étape « Votre matière »), même calcul. */
  tarifImprime(w, h, prixM2 = this.prixM2) {
    const m2 = (w * h) / 10000;
    const facture = Math.max(m2, this.minM2);
    const surface = m2 < this.minM2 ? `${mbDeci.format(m2)} m², facturé ${this.minM2} m² minimum` : `${mbDeci.format(m2)} m²`;
    if (!prixM2) return { m2, prix: 'Au panier', surface, barre: '', remise: '', centimes: null };
    // entiers seulement : un produit en flottant se trompe d'un centime sur les demi-centimes (110 × 275 cm à 89 €)
    const surfaceCm2 = Math.max(Math.round(w) * Math.round(h), this.minM2 * 10000);
    const centimes = Math.floor((surfaceCm2 * Math.round(prixM2 * 100) + 5000) / 10000);
    const palier = MB_PALIERS.find((p) => centimes >= p.seuil);
    if (palier) {
      const final = centimes - Math.floor((centimes * palier.pct) / 100); // le montant de la réduction est tronqué au centime puis soustrait, comme Shopify (vérifié sur un vrai panier : 2 684,24 − 536,84 = 2 147,40)
      return { m2, prix: mbEuros.format(final / 100), surface, barre: mbEuros.format(centimes / 100), remise: `Tarif grands murs : −${palier.pct} %`, centimes, palier };
    }
    return { m2, prix: mbEuros.format(centimes / 100), surface, barre: '', remise: '', centimes, palier: null };
  }

  majCartes() {
    const [A, B] = this.el.cartes;
    if (!A || !B || !this.pc) return;
    /* Avant vos mesures (04/10), les deux tuiles sont visibles : seules leurs vignettes sont atténuées, et elles ne montrent
       aucun chiffre de l'exemple (qu'on prendrait pour le vôtre) ; « L'œuvre entière » ne se choisit pas encore. */
    const pret = this.dataset.etat === 'pret';
    const entiere = pret && this.pe.ok && !this.unique;
    const pct = Math.round(this.visible * 100);
    const rapport = `${this.pc.w / this.pc.h}`;
    const remplir = (carte, dims, t, ligne) => {
      carte.querySelector('[data-mb-carte-dims]').textContent = pret ? dims : '';
      carte.querySelector('[data-mb-carte-prix]').textContent = pret ? (t ? t.prix : '—') : '';
      carte.querySelector('[data-mb-carte-ligne]').textContent = pret ? ligne : 'Indiquez vos dimensions.';
      carte.style.setProperty('--mb-carte-r', rapport);
    };
    const cm = (n) => mbCm.format(n);
    remplir(A, `${cm(this.pc.w)} × ${cm(this.pc.h)} cm`, this.tarifImprime(this.pc.w, this.pc.h),
      this.visible >= MB_SEUIL_ENTIER ? 'D’un bord à l’autre, l’œuvre presque entière.' : `D’un bord à l’autre. L’œuvre est recadrée : vous en gardez ${pct} %, la partie de votre choix.`);
    remplir(B, `${cm(this.pe.w)} × ${cm(this.pe.h)} cm`, this.pe.ok ? this.tarifImprime(this.pe.w, this.pe.h) : null,
      this.pe.ok ? `Rien n’est coupé. Un panneau de ${cm(this.pe.w)} × ${cm(this.pe.h)} cm : une bande de mur peint reste visible ${this.pe.h < this.pc.h ? 'en haut ou en bas' : 'sur les côtés'}.` : 'Pas possible sur ce mur.');
    if (!this.pe.ok) { B.querySelector('[data-mb-carte-dims]').textContent = ''; }
    B.hidden = pret && this.unique;
    B.dataset.indisponible = !pret || this.pe.ok ? 'non' : 'oui';
    const rb = B.querySelector('input'); const ra = A.querySelector('input');
    rb.disabled = !entiere;
    ra.checked = this.rendu === 'tout'; rb.checked = this.rendu === 'entiere';
    // vignettes
    const va = A.querySelector('.mb-carte__vignette');
    va.style.backgroundSize = `${100 / this.fw}% ${100 / this.fh}%`;
    va.style.backgroundPosition = `${this.fw < 1 ? (this.borner(this.x0, 1 - this.fw) / (1 - this.fw)) * 100 : 0}% ${this.fh < 1 ? (this.borner(this.y0, 1 - this.fh) / (1 - this.fh)) * 100 : 0}%`;
    const pb = B.querySelector('.mb-carte__panneau');
    pb.style.width = `${(this.pe.w / this.pc.w) * 100}%`; pb.style.height = `${(this.pe.h / this.pc.h) * 100}%`;
    if (this.el.consigne3) this.el.consigne3.textContent = '';
  }

  /* Glisser le panneau dans l'aperçu du mur (rendu « L'œuvre entière », visualisation seulement) */
  glisserPanneau() {
    const mur = this.el.mur;
    let depart = null;
    mur.addEventListener('pointerdown', (e) => {
      if (this.rendu !== 'entiere' || !this.pe || !this.pe.ok || e.button > 0) return;
      depart = { x: e.clientX, y: e.clientY, bx: this.bx, by: this.by, id: e.pointerId };
      mur.setPointerCapture(e.pointerId);
    });
    mur.addEventListener('pointermove', (e) => {
      if (!depart || e.pointerId !== depart.id) return;
      const b = mur.getBoundingClientRect();
      const { w: pw, h: ph } = this.pe, { w: Lp, h: Hp } = this.pc;
      if (pw < Lp) this.bx = this.borner(depart.bx + (e.clientX - depart.x) / (b.width * ((Lp - pw) / Lp)), 1);
      if (ph < Hp) this.by = this.borner(depart.by + (e.clientY - depart.y) / (b.height * ((Hp - ph) / Hp)), 1);
      this.poserPanneau();
    });
    const lacher = () => { depart = null; };
    mur.addEventListener('pointerup', lacher);
    mur.addEventListener('pointercancel', lacher);
    mur.addEventListener('lostpointercapture', lacher);
  }

  /* Découpe en lés, en millimètres pour éviter les arrondis : des lés entiers de 76,2 cm,
     puis un dernier lé à sa largeur réelle (300 cm = 3 × 76,2 cm + 71,4 cm). */
  decoupe() {
    const mur = Math.round(this.imp.w * 10);
    const entiers = Math.floor((mur - 1) / MB_LE_MM);
    return { entiers, dernier: mur - entiers * MB_LE_MM };
  }

  tracerLes() {
    const les = this.el.les;
    les.classList.remove('mb-trace');
    les.replaceChildren();
    const { entiers, dernier } = this.decoupe();
    const mur = entiers * MB_LE_MM + dernier;
    for (let k = 1; k <= entiers; k++) {
      const le = document.createElement('i');
      le.className = 'mb-cadreur__le';
      le.style.left = `${((k * MB_LE_MM) / mur) * 100}%`;
      le.style.setProperty('--d', `${k * 90}ms`);
      les.append(le);
    }
    if (this.el.decoupe) {
      const cm = (mm) => mbCm.format(mm / 10);
      this.el.decoupe.textContent = dernier === MB_LE_MM
        ? `${entiers + 1} lé${entiers ? 's' : ''} de ${cm(MB_LE_MM)} cm`
        : entiers === 0
          ? `1 lé de ${cm(dernier)} cm`
          : `${entiers + 1} lés : ${entiers} × ${cm(MB_LE_MM)} cm + 1 × ${cm(dernier)} cm`;
    }
    void les.offsetWidth;
    requestAnimationFrame(() => les.classList.add('mb-trace'));
  }

  afficherPrix() {
    const reel = !!(this.L && this.H);
    if (!this.imp) {
      this.el.prix.textContent = '—';
      this.el.surface.textContent = 'Le prix s’affiche dès vos dimensions';
      this.el.barre.hidden = true; this.el.remise.hidden = true;
      if (this.el.recap) this.el.recap.textContent = '';
      this.horsLimite = false;
      this.afficherProjet(null);
      this.prixVivant(false);
      this.majVoirChezMoi();
      return;
    }
    const avant = this.dataset.exemple;
    this.dataset.exemple = reel ? 'non' : 'oui';
    if (reel && avant === 'oui') this.devoiler();
    let t = this.tarifImprime(this.imp.w, this.imp.h);
    /* D'autres murs sont déjà dans le panier : ce mur s'affiche à son plein tarif, la réduction du projet entier
       (une seule, la plus avantageuse) se lit dans le récapitulatif juste en dessous. */
    if (this.panierMurs.length && t.centimes != null) t = { ...t, prix: mbEuros.format(t.centimes / 100), barre: '', remise: '', palier: null };
    const m = (cm) => mbM.format(cm / 100);
    const entiere = reel && this.rendu === 'entiere';
    this.el.phrase.textContent = reel
      ? (entiere ? `Votre panneau de ${m(this.imp.w)} × ${m(this.imp.h)} m` : `Votre mur de ${m(this.L)} × ${m(this.H)} m`)
      : `Exemple : un mur de ${m(this.gL)} × ${m(this.gH)} m`;
    this.el.prix.textContent = t.prix;
    this.el.surface.textContent = t.surface;
    this.el.barre.textContent = t.barre; this.el.barre.hidden = !t.barre;
    this.el.remise.textContent = t.remise; this.el.remise.hidden = !t.remise;
    this.el.remise.dataset.palier = t.barre ? 'oui' : 'non';
    if (this.el.recap) this.el.recap.textContent = `${reel ? 'Imprimé' : 'Exemple imprimé'} : ${mbCm.format(this.imp.w)} × ${mbCm.format(this.imp.h)} cm · ${mbDeci.format(t.m2)} m² · ${t.prix}`;
    this.el.coteL.textContent = `${m(this.gL)} m`;
    this.el.coteH.textContent = `${m(this.gH)} m`;
    this.horsLimite = reel && this.limite();
    this.afficherProjet(reel ? t : null);
    this.prixVivant(reel);
    this.majVoirChezMoi();
  }

  /* Affichage seulement (04/10) : la pastille posée sur votre mur et la barre fixe reprennent le texte de [data-mb-prix]
     et de [data-mb-phrase], jamais un autre calcul. La pastille reste cachée tant que le mur est l'exemple. */
  prixVivant(reel) {
    const prix = this.el.prix.textContent;
    if (this.el.vivant) {
      this.el.vivant.textContent = reel ? prix : '';
      this.el.vivant.hidden = !reel;
    }
    this.dispatchEvent(new CustomEvent('mb-cadreur:prix', { bubbles: true, detail: { phrase: this.el.phrase.textContent, prix, exemple: !reel } }));
  }

  /* Votre mur se dévoile au premier dessin réel, une seule fois par page : six panneaux brume le couvrent d'un coup puis se
     retirent de droite à gauche (mb.css, 750 ms). La classe ne sert qu'au geste ; elle se retire ensuite, rien d'autre ne change. */
  devoiler() {
    if (this.devoile) return;
    this.devoile = true;
    this.classList.add('mb-devoile');
    setTimeout(() => this.classList.remove('mb-devoile'), 1100);
  }

  /* La jauge des réductions : une barre fine, le sous-total plein tarif (celui sur lequel Shopify applique les seuils)
     et une phrase calculée. Seuils et taux : MB_PALIERS, les mêmes que le calcul du prix. */
  reperes() {
    const haut = MB_PALIERS[0].seuil;
    for (const p of [...MB_PALIERS].reverse()) {
      const r = document.createElement('span');
      r.className = 'mb-jauge__repere';
      r.style.left = `${(p.seuil / haut) * 100}%`;
      r.innerHTML = `<b>${mbEntier.format(p.seuil / 100)} €</b> −${p.pct} %`;
      this.el.reperes.append(r);
      const t = document.createElement('i');
      t.className = 'mb-jauge__seuil';
      t.style.left = `${(p.seuil / haut) * 100}%`;
      this.el.jauge.querySelector('.mb-jauge__piste').append(t);
    }
  }

  /* Le récapitulatif du projet, la jauge et la phrase du palier suivant (05/10). t : le tarif de ce mur s'il est réel.
     Ce mur compte dans le projet tant qu'il n'est pas déjà au panier et qu'il reste dans la limite de finesse. */
  afficherProjet(t) {
    const courant = t && t.centimes != null && !this.ajoute && !this.horsLimite
      ? [{ nom: 'Ce mur', mur: `${this.L} × ${this.H} cm`, centimes: t.centimes, quantite: 1 }] : [];
    const lignes = [...this.panierMurs, ...courant];
    const d = mbDevis(lignes, this.panierAutres);
    this.devis = d;
    this.dataset.projet = String(d.nb);
    if (this.el.limite) {
      this.el.limite.hidden = !this.horsLimite;
      if (this.horsLimite) this.el.limite.innerHTML = this.texteLimite();
    }
    /* Au-delà de la limite, pas de bouton grisé ni de ton d'erreur : le bouton devient la demande de devis (05/10, Marcel) */
    if (this.libelleBouton == null) this.libelleBouton = this.el.bouton.textContent;
    this.el.bouton.textContent = this.horsLimite ? 'Demander mon devis au designer' : this.libelleBouton;
    this.el.bouton.dataset.devis = this.horsLimite ? 'oui' : 'non';
    if (this.el.trois) this.el.trois.hidden = d.nb < 3;
    const box = this.el.projet;
    if (box) {
      box.hidden = d.nb < 2;
      box.replaceChildren();
      if (d.nb >= 2) {
        const ligne = (gauche, droite, classe) => {
          const p = document.createElement('p');
          if (classe) p.className = classe;
          const a = document.createElement('span'); a.textContent = gauche;
          const b = document.createElement('span'); b.textContent = droite;
          p.append(a, b);
          return p;
        };
        const titre = document.createElement('p');
        titre.className = 'mb-projet__titre';
        titre.textContent = `Votre projet · ${d.nb} œuvres`;
        box.append(titre);
        for (const l of lignes) box.append(ligne(`${l.nom}${l.mur ? ` · ${l.mur}` : ''}${l.quantite > 1 ? ` · × ${l.quantite}` : ''}`, mbEuros.format((l.centimes * l.quantite) / 100), 'mb-projet__ligne'));
        if (this.panierAutres) box.append(ligne('Autres articles du panier', mbEuros.format(this.panierAutres / 100), 'mb-projet__ligne'));
        if (d.remise) box.append(ligne(`${d.remise.nom} : −${d.remise.pct} %`, `−${mbEuros.format(d.remise.montant / 100)}`, 'mb-projet__remise'));
        box.append(ligne('Total du projet', mbEuros.format(d.total / 100), 'mb-projet__total'));
      }
    }
    const j = this.el.jauge;
    if (!d.nb || !this.prixM2) { j.hidden = true; this.el.phraseJauge.textContent = ''; return; }
    j.hidden = false;
    this.el.rempli.style.transform = `scaleX(${Math.min(d.sousTotal / MB_PALIERS[0].seuil, 1)})`;
    this.el.phraseJauge.textContent = this.horsLimite ? '' : this.phrasePalier(d, lignes); // au-delà de la limite, seul le message du devis parle
  }

  /* Le palier suivant, en euros : une œuvre de plus au minimum facturé (1 m² depuis le 28/10), puis la troisième œuvre */
  phrasePalier(d, lignes) {
    const fort = MB_REMISES_MURS[0];
    if (d.nb === 1) {
      const petite = Math.floor((this.minM2 * 10000 * Math.round(this.prixM2 * 100) + 5000) / 10000);
      const avec = mbDevis([...lignes, { centimes: petite, quantite: 1 }], this.panierAutres);
      return `Ajoutez une œuvre de ${mbM.format(this.minM2)} m² : votre commande passe de ${mbEuros.format(d.total / 100)} à ${mbEuros.format(avec.total / 100)}.`;
    }
    if (d.nb === fort.murs - 1 && (!d.remise || d.remise.pct < fort.pct)) return `Encore une œuvre, et la Maison vous offre −${fort.pct} % sur l’ensemble.`;
    return '';
  }

  /* ---------- Limite de finesse (05/10) : au moins 25 DPI sur le fichier source, dans chaque sens ---------- */
  limite() {
    if (!this.srcL || !this.srcH || !this.imp) return false;
    return this.srcL * 254 < MB_DPI_SOURCE * 100 * this.imp.w || this.srcH * 254 < MB_DPI_SOURCE * 100 * this.imp.h;
  }

  texteLimite() {
    return 'Pour un mur de cette taille, un designer de la Maison prépare votre fichier : <a class="mb-lien" href="mailto:' + (this.dataset.mail || '') + '" data-mb-ecrire>écrivez-nous</a>. Nous vous répondons avec un devis pour votre mur.';
  }

  ouvrirMail(url) { window.location.href = url; }

  /* L'e-mail au designer, recalculé au clic : l'œuvre, le mur saisi, le cadrage et le passage, le prix affiché, le projet */
  mailto() {
    const titre = (this.dataset.titre || this.oeuvre).replace(/ — /g, ', '); // pas de cadratin dans les textes aux clients
    const corps = this.horsLimite ? ['Je souhaite un devis : mon mur dépasse la taille proposée en ligne.', '', `Œuvre : ${titre}`] : [`Œuvre : ${titre}`];
    if (this.matiere) corps.push(`Matière : ${this.matiere.nom}`);
    if (this.dataset.etat === 'pret' && this.L && this.H && this.imp) {
      const entiere = this.rendu === 'entiere';
      const pos = this.mode === 'h' ? (this.fw < 1 ? this.borner(this.x0, 1 - this.fw) / (1 - this.fw) : 0.5) : (this.fh < 1 ? this.borner(this.y0, 1 - this.fh) / (1 - this.fh) : 0.5);
      const passage = this.unique ? 'l’œuvre presque entière' : `passage à ${Math.round(pos * 100)} % depuis ${this.mode === 'h' ? 'la gauche' : 'le haut'} de l’œuvre`;
      corps.push(`Mon mur : ${this.L} × ${this.H} cm`);
      corps.push(`Cadrage : ${entiere ? `L’œuvre entière, panneau de ${this.imp.w} × ${this.imp.h} cm` : `Tout le mur, ${passage}`}`);
      corps.push(`Taille imprimée : ${this.imp.w} × ${this.imp.h} cm`);
      corps.push(`Prix affiché : ${this.el.prix.textContent.replace(/[  ]/g, ' ')}`);
    }
    if (this.panierMurs.length) {
      corps.push('', 'Déjà dans mon panier :');
      for (const l of this.panierMurs) corps.push(`${l.nom}${l.mur ? `, ${l.mur}` : ''}${l.quantite > 1 ? `, ×${l.quantite}` : ''} : ${mbEuros.format((l.centimes * l.quantite) / 100).replace(/[  ]/g, ' ')}`);
    }
    if (this.devis && this.devis.nb >= 2) corps.push(`Total du projet affiché : ${mbEuros.format(this.devis.total / 100).replace(/[  ]/g, ' ')}`);
    corps.push('', 'Joignez une photo de votre mur prise de face.');
    const objet = this.dataset.objet || `Mon projet · ${titre}`;
    return `mailto:${this.dataset.mail || ''}?subject=${encodeURIComponent(objet)}&body=${encodeURIComponent(corps.join('\r\n'))}`;
  }

  /* « Habiller une autre pièce » : les champs se vident, le premier mur reste dans le panier et dans le récapitulatif */
  autrePiece() {
    this.el.L.value = ''; this.el.H.value = '';
    this.el.verif.checked = false;
    this.message('');
    this.lire(false);
    if (this.el.autre) this.el.autre.hidden = false;
    const etape = this.querySelector('[data-etape="mur"]') || this.el.L;
    etape.scrollIntoView({ block: 'start' });
    this.el.L.focus({ preventScroll: true });
  }

  /* ---------- Formats de départ ---------- */
  formats() {
    for (const b of this.el.formats) {
      const L = parseInt(b.dataset.l, 10), H = parseInt(b.dataset.h, 10);
      if (!(L > 0 && H > 0)) { b.hidden = true; continue; }
      b.querySelector('[data-mb-format-dims]').textContent = `${mbM.format(L / 100)} × ${mbM.format(H / 100)} m`;
      b.addEventListener('click', () => this.poserMesures(L, H));
    }
  }

  pressoirs() {
    const L = this.valeur(this.el.L), H = this.valeur(this.el.H);
    for (const b of this.el.formats) b.setAttribute('aria-pressed', String(L === parseInt(b.dataset.l, 10) && H === parseInt(b.dataset.h, 10)));
  }

  /* Prix et surface d'un mur (dimensions saisies, marge de 10 cm ajoutée, rendu « Tout le mur »), sans rien afficher :
     une seule règle pour le cadreur et les molettes */
  tarif(L, H) {
    let w = L + MB_MARGE; let h = H + MB_MARGE;
    if (this.rendu === 'entiere') { // même règle que calculer() : le rendu gardé s'applique aux molettes
      const r = w / h;
      const [pw, ph] = r >= this.a ? [Math.round(h * this.a), h] : [w, Math.round(w / this.a)];
      if (ph >= MB_ENTIERE.hMin && ph <= MB_ENTIERE.hMax && Math.min(r / this.a, this.a / r) < MB_SEUIL_ENTIER) { w = pw; h = ph; }
    }
    const t = this.tarifImprime(w, h);
    return { prix: t.prix, surface: t.surface };
  }

  /* ---------- Molettes du mobile (assets/mb-molettes.js) ----------
     Elles lisent les champs à l'ouverture et y écrivent comme une saisie au clavier validée.
     Les champs restent la source de vérité. */
  get bornes() { return MB_BORNES; }

  /* Une saisie refusée (NaN : « 76,2 », « 3 m 50 ») compte comme pas de mesure : la paire part de l'exemple */
  mesuresSaisies() {
    const lu = (champ) => { const v = this.valeur(champ); return Number.isInteger(v) ? v : null; };
    return {
      L: lu(this.el.L), H: lu(this.el.H),
      exempleL: parseInt(this.el.L.placeholder, 10) || 300,
      exempleH: parseInt(this.el.H.placeholder, 10) || 250,
    };
  }

  /* L, H : entiers déjà bornés, ou null pour une dimension que le client n'a pas choisie (champ laissé tel quel).
     Jamais autre chose qu'un entier dans un champ. Aucun événement émis (lire ne tourne qu'une fois, les lés ne se
     tracent qu'une fois), aucun focus, aucun défilement. */
  poserMesures(L, H) {
    if (Number.isInteger(L)) this.el.L.value = String(L);
    if (Number.isInteger(H)) this.el.H.value = String(H);
    this.lire(true);
    if (!this.el.L.value && !this.el.H.value) this.erreur(this.valider().texte); // rien choisi : on le dit, comme à l'ajout
  }

  /* ---------- Glisser : souris, doigt ---------- */
  ressort(oui) { this.classList.toggle('mb-ressort', oui); }

  glisser() {
    const vue = this.el.vue;
    let depart = null;
    vue.addEventListener('pointerdown', (e) => {
      if (this.dataset.etat !== 'pret' || e.button > 0) return;
      depart = { x: e.clientX, y: e.clientY, x0: this.x0, y0: this.y0, id: e.pointerId };
      vue.setPointerCapture(e.pointerId);
      this.ressort(false);
      this.el.cadre.classList.add('mb-actif');
    });
    vue.addEventListener('pointermove', (e) => {
      if (!depart || e.pointerId !== depart.id || !this.geo) return;
      const { Aw, Hs, panoramique } = this.geo;
      if (this.mode === 'h') {
        const dx = (e.clientX - depart.x) / Aw;
        const brut = panoramique ? depart.x0 - dx : depart.x0 + dx;
        this.x0 = this.elastique(brut, 1 - this.fw);
      } else {
        const dy = (e.clientY - depart.y) / Hs;
        this.y0 = this.elastique(depart.y0 + dy, 1 - this.fh);
      }
      this.poser();
    });
    const lacher = (e) => {
      if (!depart || (e && e.pointerId !== depart.id)) return;
      depart = null;
      this.el.cadre.classList.remove('mb-actif');
      this.ressort(true);
      this.x0 = this.borner(this.x0, 1 - this.fw);
      this.y0 = this.borner(this.y0, 1 - this.fh);
      this.poser();
      this.majVoirChezMoi();
    };
    vue.addEventListener('pointerup', lacher);
    vue.addEventListener('pointercancel', lacher);
    vue.addEventListener('lostpointercapture', lacher);
  }

  /* Au-delà du bord, le cadre résiste un peu puis revient (ressort) */
  elastique(v, max) {
    if (v < 0) return -Math.min(-v * 0.3, 0.03);
    if (v > max) return max + Math.min((v - max) * 0.3, 0.03);
    return v;
  }

  clavier() {
    this.el.cadre.addEventListener('keydown', (e) => {
      if (this.dataset.etat !== 'pret') return;
      const pas = e.shiftKey ? 0.05 : 0.01;
      const h = this.mode === 'h';
      let x = this.x0; let y = this.y0;
      if (e.key === (h ? 'ArrowLeft' : 'ArrowUp')) { if (h) x -= pas; else y -= pas; }
      else if (e.key === (h ? 'ArrowRight' : 'ArrowDown')) { if (h) x += pas; else y += pas; }
      else if (e.key === 'Home') { x = 0; y = 0; }
      else if (e.key === 'End') { x = 1 - this.fw; y = 1 - this.fh; }
      else return;
      e.preventDefault();
      this.ressort(true);
      this.x0 = this.borner(x, 1 - this.fw);
      this.y0 = this.borner(y, 1 - this.fh);
      this.poser();
      this.majVoirChezMoi();
    });
  }

  /* ---------- Voir chez moi (docs/voir-chez-moi.md) ----------
     Le module reçoit le cadrage à chaque changement de dimensions et à la fin de chaque déplacement
     du cadre, jamais pendant le glisser. Les deux scripts sont différés : il peut arriver après nous. */
  voirChezMoi() {
    if (!this.querySelector('mb-voir-chez-moi')) return;
    customElements.whenDefined('mb-voir-chez-moi').then(() => this.majVoirChezMoi());
    this.addEventListener('mb-vcm:commander', () => {
      /* Le client vient de toucher « Je compose mon mur » dans la couche : on lui dit ce qui reste à faire */
      if (!this.el.verif.checked) this.message(`Dernière étape : cochez la case, puis « ${this.el.bouton.textContent.trim()} ».`);
      this.amenerCase();
    });
    this.addEventListener('mb-vcm:recadrer', () => {
      this.el.vue.scrollIntoView({ block: 'center' });
      this.el.cadre.focus({ preventScroll: true });
    });
  }

  /* La case de vérification amenée au centre de l'écran, avec le focus : au refus sans la case, et depuis Voir chez moi */
  amenerCase() {
    (this.el.verif.closest('.mb-verif') || this.el.verif).scrollIntoView({ block: 'center' });
    this.el.verif.focus({ preventScroll: true });
  }

  majVoirChezMoi() {
    if (this.dataset.etat === 'pret') this.majCartes();
    const vcm = this.querySelector('mb-voir-chez-moi');
    if (vcm && vcm.maj) vcm.maj(this.dataset.etat === 'pret' ? this.cadrage(true) : null);
  }

  /* ---------- Messages ---------- */
  erreur(html) {
    const el = this.el.erreur;
    el.innerHTML = html;
    el.hidden = !html;
  }

  message(html, type = '') {
    /* 24/10 : Apippa absent, sa phrase reste affichée à la place d'un message vide, tant que l'ajout est bloqué */
    const apippa = !html && this.apippa === 'absent';
    if (apippa) { html = this.texteApippa(); type = 'erreur'; }
    this.el.message.innerHTML = html;
    if (apippa) this.el.message.dataset.apippa = 'oui'; else delete this.el.message.dataset.apippa;
    if (type) this.el.message.dataset.type = type; else delete this.el.message.dataset.type;
  }

  /* ---------- Contrat avec l'atelier ---------- */
  /* v2 (02/10) : largeur_cm et hauteur_cm sont la taille IMPRIMÉE (marge de 10 cm comprise), celle qu'Apippa facture.
     mode « tout » : le cadre glisse sur l'œuvre ; mode « entiere » : l'œuvre complète (0,0 → 1,1) à la taille du panneau.
     mur_largeur_cm et mur_hauteur_cm : le mur saisi par le client. `pourMur` : cadrage « Tout le mur » seul, pour Voir chez moi. */
  cadrage(pourMur = false) {
    const r4 = (v) => Math.round(v * 10000) / 10000;
    const entiere = this.rendu === 'entiere' && !pourMur;
    const x0 = entiere ? 0 : this.borner(this.x0, 1 - this.fw);
    const y0 = entiere ? 0 : this.borner(this.y0, 1 - this.fh);
    const taille = entiere ? this.pe : this.pc;
    return JSON.stringify({
      v: 2, oeuvre: this.oeuvre, mode: entiere ? 'entiere' : 'tout',
      x0: r4(x0), y0: r4(y0), x1: r4(entiere ? 1 : x0 + this.fw), y1: r4(entiere ? 1 : y0 + this.fh),
      largeur_cm: taille.w, hauteur_cm: taille.h,
      mur_largeur_cm: this.L, mur_hauteur_cm: this.H, marge_cm: MB_MARGE,
    });
  }

  /* Champs d'Apippa : repérés par leur nom dans le formulaire produit, jamais par une classe interne de l'appli.
     Depuis le 03/10, Apippa les nomme « Largeur imprimée (cm) » et « Hauteur imprimée (cm) ».
     24/10 : plus aucun repli. Il faut ces deux champs ET le jeton d'Apippa (properties[_cpc_custom_cart_item]) dans le
     formulaire, sinon rien ne part au panier. Avant, le moteur prenait à défaut n'importe quel champ « largeur / hauteur »
     de la page (#sig-largeur sur les Signatures) : sans calculateur, la ligne partait au prix d'un seul m².
     24/10, relecture : le champ « Surface (m²) » d'Apippa est exigé aussi (contrôlé avant l'envoi). */
  trouverApippa(form) {
    if (!form) return null;
    const champs = [...form.elements].filter((i) => i.tagName === 'INPUT' && !i.closest('mb-cadreur'));
    const nom = (i) => String(i.name || '').toLowerCase();
    const saisie = (i) => /^(number|text|tel|)$/.test(i.type);
    const largeur = champs.find((i) => saisie(i) && /^properties\[largeur imprim/.test(nom(i)));
    const hauteur = champs.find((i) => saisie(i) && /^properties\[hauteur imprim/.test(nom(i)));
    const surface = champs.find((i) => /^properties\[surface/.test(nom(i)));
    const jeton = champs.find((i) => nom(i) === 'properties[_cpc_custom_cart_item]');
    return largeur && hauteur && surface && jeton && largeur !== hauteur ? { largeur, hauteur, surface, jeton } : null;
  }

  /* 24/10, relecture (défaut 1) : la configuration que la cliente voit au clic (mur, rendu, matière, taille imprimée,
     cadrage, prix). L'ajout ne part que si c'est encore elle au moment de l'envoi. */
  configuration() {
    const r4 = (x) => Math.round(x * 10000) / 10000;
    const t = this.imp ? this.tarifImprime(this.imp.w, this.imp.h) : null;
    return JSON.stringify([this.dataset.etat, this.L, this.H, this.rendu, this.matiere && this.matiere.id,
      this.imp && this.imp.w, this.imp && this.imp.h, r4(this.x0), r4(this.y0), t && t.centimes, t && t.prix]);
  }

  /* Après une attente : même mur qu'au clic, valide, case cochée. Sinon rien ne part et le bouton redevient cliquable. */
  memeMur(vue) {
    const ok = this.configuration() === vue && this.dataset.etat === 'pret' && this.valider().ok && this.el.verif.checked
      && Number.isInteger(this.L) && Number.isInteger(this.H) && this.imp && this.imp.w > 0 && this.imp.h > 0;
    if (ok) return true;
    this.el.bouton.removeAttribute('aria-busy');
    this.message('Votre mur a changé pendant le calcul du prix. Vérifiez-le, puis ajoutez-le au panier.', 'erreur');
    return false;
  }

  formulaire() {
    return document.querySelector('form[data-type="add-to-cart-form"]');
  }

  texteApippa() {
    return `Le calcul du prix ne s’est pas chargé. Rechargez la page, ou <a href="${this.contact}">écrivez-nous</a> : nous préparons votre commande.`;
  }

  /* Apippa guetté (24/10) : toutes les 250 ms pendant le délai d'attente, puis toutes les 2 s, jusqu'à ses champs (trouvés :
     on s'arrête). Délai passé sans eux : bouton désactivé et message. Ils arrivent plus tard : le bouton se rouvre. */
  guetterApippa() {
    if (this.apippaLimite == null) this.apippaLimite = Date.now() + MB_APIPPA_ATTENTE;
    clearTimeout(this.minuteurApippa);
    const guetter = () => {
      if (this.trouverApippa(this.formulaire())) { this.etatApippa('pret'); return; }
      this.etatApippa(Date.now() >= this.apippaLimite ? 'absent' : 'attente');
      this.minuteurApippa = setTimeout(guetter, Date.now() < this.apippaLimite ? 250 : 2000);
    };
    guetter();
  }

  /* attente → pret, ou attente → absent → pret : data-apippa sur <mb-cadreur>, bouton désactivé seulement si absent */
  etatApippa(etat) {
    if (this.apippa === etat) return;
    this.apippa = etat;
    this.dataset.apippa = etat;
    this.el.bouton.disabled = etat === 'absent';
    if (etat === 'absent') {
      console.warn('[mb-cadreur] Champs Apippa introuvables : ajout au panier bloqué pour ne pas vendre au mauvais prix.');
      this.message('');
    } else if (this.el.message.dataset.apippa) this.message('');
  }

  /* Au clic, Apippa pas encore là : on l'attend jusqu'à la fin du délai de chargement, et au moins 3 s */
  async attendreApippa() {
    const fin = Math.max(this.apippaLimite || 0, Date.now() + 3000);
    let a = this.trouverApippa(this.formulaire());
    while (!a && Date.now() < fin) {
      await new Promise((r) => setTimeout(r, 200));
      a = this.trouverApippa(this.formulaire());
    }
    return a;
  }

  remplir(champ, valeur) {
    const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set;
    champ.focus({ preventScroll: true });
    setter.call(champ, String(valeur));
    for (const type of ['input', 'change', 'keyup', 'blur']) champ.dispatchEvent(new Event(type, { bubbles: true }));
  }

  propriete(form, nom, valeur) {
    let champ = form.querySelector(`input[type="hidden"][name="properties[${nom}]"]`);
    if (!champ) {
      champ = document.createElement('input');
      champ.type = 'hidden';
      champ.name = `properties[${nom}]`;
      champ.dataset.mbCadreur = '';
      form.append(champ);
    }
    champ.value = valeur;
  }

  /* Propriétés visibles de la ligne, lues au paiement, dans les e-mails et par le panier (le Liquid du panier ne voit
     pas _cadrage : vérifié le 03/10 sur le vrai panier). « Cadrage » ne vaut plus jamais « Choisi par vous ». */
  proprietesVisibles() {
    return {
      'Votre mur': `${this.L} × ${this.H} cm`,
      Cadrage: this.rendu === 'entiere' ? 'L’œuvre entière' : 'Tout le mur',
    };
  }

  async ajouter() {
    this.message('');
    const v = this.valider();
    if (!v.ok) {
      this.erreur(v.texte);
      v.champ.focus();
      return;
    }
    if (this.horsLimite) { // au-delà de la finesse du fichier source : jamais d'ajout, le designer prépare le devis
      this.ouvrirMail(this.mailto());
      return;
    }
    if (!this.el.verif.checked) {
      this.message('Cochez la case pour confirmer vos dimensions et votre cadrage.', 'erreur');
      this.amenerCase();
      return;
    }
    const vue = this.configuration(); // 24/10, relecture : ce que la cliente voit au clic
    let form = this.formulaire();
    let apippa = this.trouverApippa(form);
    if (!apippa && this.apippa !== 'absent') { // 24/10 : Apippa se charge encore, on l'attend (jamais d'ajout sans lui)
      if (this.attenteApippa) return;
      this.attenteApippa = true;
      this.el.bouton.setAttribute('aria-busy', 'true');
      this.message('Le calcul du prix se charge, un instant.');
      apippa = await this.attendreApippa();
      form = this.formulaire();
      this.attenteApippa = false;
      if (apippa) this.message('');
    }
    if (!form || !apippa) {
      this.el.bouton.removeAttribute('aria-busy');
      this.apippaLimite = Date.now();
      this.etatApippa('absent'); // bouton désactivé et message ; il se rouvre si Apippa arrive
      this.message('');
      this.guetterApippa();
      return;
    }
    this.etatApippa('pret');
    if (!this.memeMur(vue)) return; // le mur a changé pendant l'attente : rien ne part
    if (!this.synchroniserVariante()) {
      this.el.bouton.removeAttribute('aria-busy');
      this.message(`La matière n’a pas pu être enregistrée. Rechargez la page, ou <a href="${this.contact}">écrivez-nous</a>.`, 'erreur');
      return;
    }
    this.el.bouton.setAttribute('aria-busy', 'true');
    this.remplir(apippa.largeur, this.imp.w);
    this.remplir(apippa.hauteur, this.imp.h);
    this.propriete(form, '_cadrage', this.cadrage());
    for (const [nom, valeur] of Object.entries(this.proprietesVisibles())) this.propriete(form, nom, valeur);
    this.el.bouton.focus({ preventScroll: true });
    await new Promise((r) => setTimeout(r, 400)); /* le temps qu'Apippa recalcule */
    const lu = (c) => parseFloat(String(c.value).replace(',', '.').replace(/[^\d.]/g, ''));
    const surfaceVue = (this.imp.w * this.imp.h) / 10000; // Apippa écrit largeur × hauteur, au centième (relevé le 24/10)
    if (lu(apippa.largeur) !== this.imp.w || lu(apippa.hauteur) !== this.imp.h || !(Math.abs(lu(apippa.surface) - surfaceVue) < 0.006)) { // Apippa a écrêté, refusé ou laissé vide une valeur : jamais d'ajout à un autre prix que celui affiché
      this.el.bouton.removeAttribute('aria-busy');
      this.message(`Vos dimensions n’ont pas été prises en compte. Réessayez, ou <a href="${this.contact}">écrivez-nous</a> : nous préparons votre commande.`, 'erreur');
      return;
    }
    if (!this.memeMur(vue)) return; // 24/10, relecture : dernier regard sur le mur, juste avant l'envoi
    this.attente = true;
    clearTimeout(this.minuteur);
    this.minuteur = setTimeout(() => { if (this.attente) this.fin(false); }, 12000);
    const soumettre = form.querySelector('[type="submit"], button[name="add"]');
    if (soumettre) soumettre.click();
    else if (form.requestSubmit) form.requestSubmit();
    else form.submit();
  }

  panier() {
    document.addEventListener('shopify:cart:lines-update', (e) => {
      if (!this.attente) return;
      Promise.resolve(e.promise).then(() => this.fin(true), () => this.fin(false));
    });
    document.addEventListener('shopify:cart:error', () => { if (this.attente) this.fin(false); });
    document.addEventListener('mb-cadreur:envoi-refuse', () => { if (this.attente) this.fin(false, this.texteApippa()); });
  }

  fin(ok, texte = '') {
    this.attente = false;
    clearTimeout(this.minuteur);
    this.el.bouton.removeAttribute('aria-busy');
    if (ok) {
      this.ajoute = true;
      this.message('Votre mur est dans votre panier. <a href="/cart">Voir le panier</a> <button type="button" class="mb-cadreur__autre-bouton" data-mb-autre>Habiller une autre pièce</button>', 'ok');
      this.lirePanier();
    }
    else this.message(texte || `L’ajout n’a pas abouti. Réessayez, ou <a href="${this.contact}">écrivez-nous</a>.`, 'erreur');
  }
}

if (!customElements.get('mb-cadreur')) customElements.define('mb-cadreur', MbCadreur);
